import { LogManager } from '@src/utility/Logger';

import type { Timeline } from '@src/editor/models/Timeline';
import type { VideoResolution, VideoSource } from '@src/types.ts/video';

const logger = LogManager.getLogger('service:PublishSourceService');

/**
 * Function that replaces original video element source URLs with the
 * URL matching the requested resolution level.
 */
type ReplaceUrlFn = (
  source: VideoSource,
  resLevel: VideoResolution,
) => VideoSource;

/**
 * PublishSourceService
 *
 * Consolidates the source-preparation pipeline shared by both
 * `VideoCreatorStore.publishVideo` and `ClipVideoCreatorAdapter.publishVideo`.
 *
 * The pipeline is:
 *   1. `timeline.toRenderJSON()` — serialise the editor model with full
 *      keyframe merge and rendering-grade timing adjustments.
 *   2. `replaceUrl(source, resLevel)` — swap original video element URLs
 *      with the URL for the requested resolution.
 *
 * Stateless — safe to instantiate per-call or share as a singleton.
 */
export class PublishSourceService {
  /**
   * Prepare a render-ready video source for publishing.
   *
   * @param timeline         The timeline to serialise
   * @param resLevel         Desired resolution level (passed through to replaceUrl)
   * @param replaceUrl       Function to swap video source URLs for the target res
   * @returns The fully prepared VideoSource ready for the Creatomate render API
   */
  prepareSourceForPublish(
    timeline: Timeline,
    resLevel: VideoResolution,
    replaceUrl: ReplaceUrlFn,
  ): VideoSource {
    logger.debug('prepareSourceForPublish: serialising render source');

    // 1. Serialise from the editor model (full keyframe merge + timing adjustments)
    let source: VideoSource = timeline.toRenderJSON();

    // 2. Replace video URLs with the target resolution
    source = replaceUrl(source, resLevel);

    return source;
  }
}
